import React, { useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Season } from "@mysnowscape/snow-lib";
import { useFeatureFlag, User } from "configcat-react";

import RouteCard from "../../../components/Cards/RouteCard";
import StartARouteCard from "../../../components/Cards/StartARouteCard";
import FilterBar from "../../../components/Filters/FilterBar";
import SearchBarWithFilters from "../../../components/Filters/SearchBarWithFilters";
import HeaderBar from "../../../components/Header/HeaderBar";
import LoadingIcon from "../../../components/Loaders/LoadingIcon";
import MultipleChoiceModal from "../../../components/Modals/MultipleChoiceModal";
import TextInputModal from "../../../components/Modals/TextInputModal";
import { confirmWrapper } from "../../../components/Modals/ConfirmWrapper";
import { multipleChoiceConfirmWrapper } from "../../../components/Modals/MultipleChoiceConfirmWrapper";
import RouteBuilderEntry from "./RouteBuilder/RouteBuilderEntry";
import DraftsList from "./RouteBuilder/components/DraftsList";
import RouteSelectionCheckbox from "./RouteBuilder/components/RouteSelectionCheckbox";
import RoutesGroupTree from "./RouteBuilder/components/RoutesGroupTree";

import axiosRoutes from "../../../services/axios/routes";
import { formatErrorMessage } from "../../../services/axios/APIError";
import flash from "../../../services/flash";
import errors from "../../../services/errors";
import { persistedRouteType } from "../../../utils/routeType";
import useDownloadHelper from "../../../hooks/useDownloadHelper";
import { confirmPrintRoutes } from "../../../components/Modals/PrintRoutesConfirmModal";

import ReorderCardWrapper from "../../../components/Cards/ReorderCardWrapper";
import TwistedArrow from "../../../resources/twisted-arrow.svg";
import SparkAiIcon from "../../../resources/sparkai.svg";
import {
  deleteGroupPathFromTags,
  formatRouteGroupUpdateError,
  groupSegmentError,
  isGroupPathOrDescendant,
  moveRouteGroupMembership,
  normalizeRouteTags,
  renameGroupPathInTags,
} from "./RouteBuilder/lib/routeGroups";
import { saveRouteGroupDrop } from "./RouteBuilder/lib/routeGroupDrag";

function FlatSeasonSection({
  season,
  routes,
  selectedRouteIds,
  onToggleSelection,
  onOpen,
  onReorder,
  selectable = true,
}) {
  if (routes.length === 0) return null;
  const landscape = season === Season.landscape;
  return (
    <div className="w-full">
      <div
        className={`mb-4 flex w-full justify-between ${
          landscape ? "text-landscape-primary" : "text-snow-primary"
        }`}
      >
        <div className="flex-1 text-3xl font-bold capitalize">
          {season} Routes
        </div>
        <div className="mb-2.5 flex flex-row justify-end pr-3 align-top">
          <span className="mr-2 text-lg font-bold font-hand">
            Order your routes here
          </span>
          <img alt="-->" className="relative top-4 w-10" src={TwistedArrow} />
        </div>
      </div>
      <div
        className={`border-l-[5px] pl-2 ${
          landscape ? "border-landscape-primary" : "border-snow-secondary"
        }`}
      >
        {routes.map((route, index) => {
          const card = (
            <RouteCard
              description={route.details}
              numberOfJobs={route.jobIds?.length ?? route.jobs?.length ?? 0}
              isActive={route.isActive}
              isTemporary={persistedRouteType(route) === "temporary"}
              onClick={() => onOpen(route)}
              name={route.name}
            />
          );
          return (
            <ReorderCardWrapper
              key={route._id}
              downPressed={() => onReorder(route, index + 1)}
              upPressed={() => onReorder(route, index - 1)}
              isActive={route.isActive}
            >
              {selectable ? (
                <div className="flex items-start gap-3">
                  <RouteSelectionCheckbox
                    state={
                      selectedRouteIds.has(route._id) ? "checked" : "unchecked"
                    }
                    label={`Select ${route.name}`}
                    testId={`list-route-check-${route._id}`}
                    className="mt-4"
                    onChange={() => onToggleSelection(route._id)}
                  />
                  <div className="min-w-0 flex-1">{card}</div>
                </div>
              ) : (
                card
              )}
            </ReorderCardWrapper>
          );
        })}
      </div>
    </div>
  );
}

export const AdminRoutesPage = (props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const builderRef = useRef(null);
  const downloadPDF = useDownloadHelper("/route/print");
  const currentUser = props.currentUser;
  const companyId = currentUser.currentCompanyId;
  const userId = currentUser._id ?? currentUser.uid;
  const { value: routeBuilderWeb } = useFeatureFlag(
    "routeBuilderWeb",
    true,
    userId
      ? new User(userId, undefined, undefined, {
          companyId: companyId ?? "unknown",
        })
      : undefined,
  );
  const routesKey = useMemo(() => ["routes", companyId], [companyId]);

  const [currentFilter, setCurrentFilter] = useState("all");
  const [displayChooseSeasonModal, setDisplayChooseSeasonModal] =
    useState(false);
  const [blankRouteSeason, setBlankRouteSeason] = useState(null);
  const [searchPhrase, setSearchPhrase] = useState("");
  const [layout, setLayout] = useState("groups");
  const [selectedRouteIds, setSelectedRouteIds] = useState(() => new Set());
  const [groupsBusy, setGroupsBusy] = useState(false);
  const [pendingRouteId, setPendingRouteId] = useState(null);
  const pendingRoute = useRef(null);
  const [deletingRoutes, setDeletingRoutes] = useState(false);
  const [nameModal, setNameModal] = useState(null);
  const [optimizationNoticeVisible, setOptimizationNoticeVisible] =
    useState(false);
  // Groups are derived from route tags, so a new empty group has no backend data
  // to represent it. Keep its path here until a route is moved into the group and
  // that route's tags persist the group.
  const [transientGroups, setTransientGroups] = useState(() => ({
    [Season.landscape]: new Set(),
    [Season.snow]: new Set(),
  }));

  const routesQuery = useQuery({
    queryKey: routesKey,
    queryFn: ({ signal }) => axiosRoutes.getAllRoutesAsync(companyId, signal),
    enabled: Boolean(companyId),
    refetchOnWindowFocus: pendingRouteId === null,
    refetchOnReconnect: pendingRouteId === null,
  });
  const routes = useMemo(
    () => (Array.isArray(routesQuery.data) ? routesQuery.data : []),
    [routesQuery.data],
  );
  const visibleRoutes = useMemo(() => {
    const phrase = searchPhrase.trim().toLocaleLowerCase();
    return routes.filter((route) => {
      if (currentFilter !== "all" && route.season !== currentFilter) {
        return false;
      }
      return !phrase || route.name.toLocaleLowerCase().includes(phrase);
    });
  }, [currentFilter, routes, searchPhrase]);

  const selectedRoutes = useMemo(
    () => routes.filter((route) => selectedRouteIds.has(route._id)),
    [routes, selectedRouteIds],
  );
  const selectionIssue = useMemo(() => {
    if (selectedRoutes.length === 0) return null;
    if (new Set(selectedRoutes.map((route) => route.season)).size > 1) {
      return "Select routes from one season to edit them together.";
    }
    if (new Set(selectedRoutes.map(persistedRouteType)).size > 1) {
      return "Select either recurring or one-time routes, not both.";
    }
    return null;
  }, [selectedRoutes]);

  const refreshRoutes = async () => {
    await queryClient.invalidateQueries({ queryKey: routesKey });
  };

  const handleDeleteRoutes = async () => {
    if (
      groupsBusy ||
      deletingRoutes ||
      pendingRouteId !== null ||
      selectedRoutes.length === 0
    )
      return;
    setDeletingRoutes(true);
    try {
      if (
        !(await multipleChoiceConfirmWrapper(
          `Are you sure you want to delete ${selectedRoutes.length === 1 ? "this route" : "these routes"}?`,
          { confirmLabel: "Delete" },
        ))
      )
        return;

      const deletedIds = new Set();
      await Promise.all(
        selectedRoutes.map(({ _id: routeId }) =>
          axiosRoutes.deleteRoute({ routeId }, () => deletedIds.add(routeId)),
        ),
      );
      setSelectedRouteIds(
        (previous) =>
          new Set([...previous].filter((id) => !deletedIds.has(id))),
      );
      const failedCount = selectedRoutes.length - deletedIds.size;
      void (failedCount
        ? flash.error(`Could not delete ${failedCount} route(s).`)
        : flash.success(`Deleted ${deletedIds.size} route(s).`));
      await refreshRoutes();
    } catch (error) {
      errors.report(error);
      void flash.error("Unable to finish deleting routes.");
    } finally {
      setDeletingRoutes(false);
    }
  };

  const updateTags = async (updates, successMessage) => {
    if (updates.length === 0) return true;
    setGroupsBusy(true);
    try {
      const results = await Promise.allSettled(
        updates.map(({ routeId, tags }) =>
          axiosRoutes.updateRouteTags(routeId, tags),
        ),
      );
      await refreshRoutes();
      const failed = results.filter((result) => result.status === "rejected");
      if (failed.length > 0) {
        void flash.error(
          formatRouteGroupUpdateError(failed[0].reason, failed.length),
        );
        return false;
      }
      if (successMessage) void flash.success(successMessage);
      return true;
    } catch (error) {
      errors.report(error);
      void flash.error(
        "Routes were updated, but the latest list could not be loaded.",
      );
      return false;
    } finally {
      setGroupsBusy(false);
    }
  };

  const mutateTransientGroups = (season, updater) => {
    setTransientGroups((previous) => ({
      ...previous,
      [season]: updater(new Set(previous[season])),
    }));
  };

  const openAddGroup = (season, parentPath, siblingNames) => {
    setNameModal({
      kind: "add",
      season,
      parentPath,
      siblingNames,
      defaultValue: "",
    });
  };

  const openRenameGroup = (season, node, siblingNames) => {
    setNameModal({
      kind: "rename",
      season,
      path: node.path,
      siblingNames: siblingNames.filter(
        (name) => name.toLocaleLowerCase() !== node.name.toLocaleLowerCase(),
      ),
      defaultValue: node.name,
    });
  };

  const saveGroupName = async (rawName) => {
    if (!nameModal) return;
    const error = groupSegmentError(rawName, nameModal.siblingNames);
    if (error) {
      void flash.error(error);
      return;
    }
    const name = rawName.trim();
    if (nameModal.kind === "add") {
      const path = nameModal.parentPath
        ? `${nameModal.parentPath}/${name}`
        : name;
      mutateTransientGroups(nameModal.season, (paths) => paths.add(path));
      setNameModal(null);
      return;
    }

    const parent = nameModal.path.includes("/")
      ? nameModal.path.slice(0, nameModal.path.lastIndexOf("/"))
      : "";
    const newPath = parent ? `${parent}/${name}` : name;
    const affected = routes.filter(
      (route) =>
        route.season === nameModal.season &&
        normalizeRouteTags(route.tags).some((tag) =>
          isGroupPathOrDescendant(tag, nameModal.path),
        ),
    );
    const updates = affected.map((route) => ({
      routeId: route._id,
      tags: renameGroupPathInTags(route.tags, nameModal.path, newPath),
    }));
    const ok = await updateTags(updates, `Renamed Group to “${name}”.`);
    if (!ok) return;
    mutateTransientGroups(nameModal.season, (paths) => {
      const next = new Set();
      for (const path of paths) {
        next.add(
          isGroupPathOrDescendant(path, nameModal.path)
            ? `${newPath}${path.slice(nameModal.path.length)}`
            : path,
        );
      }
      return next;
    });
    setNameModal(null);
  };

  const deleteGroup = async (season, node) => {
    const ok = await confirmWrapper(`Delete Group “${node.name}”?`, {
      description:
        "This removes the Group and its subgroups from affected routes. Other Group memberships are preserved.",
      okText: "Delete Group",
      cancelText: "Cancel",
    });
    if (!ok) return;
    const affected = routes.filter(
      (route) =>
        route.season === season &&
        normalizeRouteTags(route.tags).some((tag) =>
          isGroupPathOrDescendant(tag, node.path),
        ),
    );
    const updates = affected.map((route) => ({
      routeId: route._id,
      tags: deleteGroupPathFromTags(route.tags, node.path),
    }));
    const updated = await updateTags(updates, `Deleted Group “${node.name}”.`);
    if (!updated) return;
    mutateTransientGroups(season, (paths) => {
      for (const path of Array.from(paths)) {
        if (isGroupPathOrDescendant(path, node.path)) {
          paths.delete(path);
        }
      }
      return paths;
    });
  };

  const saveRouteDrop = async (routeId, updateRoutes, save) => {
    if (groupsBusy || deletingRoutes) return null;
    const result = await saveRouteGroupDrop({
      queryClient,
      queryKey: routesKey,
      routeId,
      pendingRoute,
      onPendingChange: setPendingRouteId,
      updateRoutes,
      save,
    });
    // A failed reload must not make a confirmed write look like a failed move.
    if (result && !result.refreshed) {
      void flash.error(
        "Route change saved, but the list could not be refreshed.",
      );
    }
    return result;
  };

  const moveRoute = async (route, sourcePath, targetPath) => {
    const tags = moveRouteGroupMembership(route.tags, sourcePath, targetPath);
    try {
      const result = await saveRouteDrop(
        route._id,
        (current) =>
          Array.isArray(current)
            ? current.map((candidate) =>
                candidate._id === route._id
                  ? { ...candidate, tags }
                  : candidate,
              )
            : current,
        () => axiosRoutes.updateRouteTags(route._id, tags),
      );
      if (!result) return;
      if (result.refreshed) {
        void flash.success(
          targetPath
            ? `Moved “${route.name}”.`
            : `Removed “${route.name}” from the Group.`,
        );
      }
      if (targetPath) {
        mutateTransientGroups(route.season, (paths) => {
          paths.delete(targetPath);
          return paths;
        });
      }
    } catch (error) {
      void flash.error(
        `The route move could not be confirmed. ${formatErrorMessage(error)}`,
      );
    }
  };

  const handleReorder = async (route, order) => {
    try {
      await axiosRoutes.orderRoute(route._id, order);
      await refreshRoutes();
    } catch (error) {
      errors.report(error);
      void flash.error(error.message);
    }
  };

  const reorderRoute = async (route, targetRouteId) => {
    const orderedSeasonRoutes = routes
      .filter((candidate) => candidate.season === route.season)
      .sort(
        (a, b) =>
          (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name),
      );
    const targetIndex = orderedSeasonRoutes.findIndex(
      (candidate) => candidate._id === targetRouteId,
    );
    const sourceIndex = orderedSeasonRoutes.findIndex(
      (candidate) => candidate._id === route._id,
    );
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
      return;
    }

    const reordered = [...orderedSeasonRoutes];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    const orderById = new Map(
      reordered.map((candidate, index) => [candidate._id, index]),
    );
    try {
      await saveRouteDrop(
        route._id,
        (current) =>
          Array.isArray(current)
            ? current.map((candidate) =>
                orderById.has(candidate._id)
                  ? { ...candidate, order: orderById.get(candidate._id) }
                  : candidate,
              )
            : current,
        () => axiosRoutes.orderRoute(route._id, targetIndex),
      );
    } catch (error) {
      errors.report(error);
      void flash.error(
        `The route order could not be confirmed. ${formatErrorMessage(error)}`,
      );
    }
  };

  const openBuilderEdit = async () => {
    if (selectionIssue || selectedRouteIds.size === 0) return;
    await builderRef.current?.openEdit(Array.from(selectedRouteIds));
  };

  const openBuilderSetup = () => {
    builderRef.current?.openSetup?.();
  };

  const openRoute = (route) => {
    if (routeBuilderWeb) {
      void builderRef.current?.openEdit([route._id]);
    } else {
      navigate(`/admin/route/${route._id}`);
    }
  };
  const handleDownloadPDF = async () => {
    if (selectedRouteIds.size === 0) return;
    const visibleSelectedIds = visibleRoutes
      .filter((r) => selectedRouteIds.has(r._id))
      .map((r) => r._id);
    const remainingSelectedIds = Array.from(selectedRouteIds).filter(
      (id) => !visibleSelectedIds.includes(id),
    );
    const routeIdsToPrint = [...visibleSelectedIds, ...remainingSelectedIds];
    const routesToPrint = routeIdsToPrint.map(
      (id) =>
        routes.find((r) => r._id === id) || {
          _id: id,
          name: "Route",
          season: "snow",
        },
    );

    const confirmed = await confirmPrintRoutes({
      routes: routesToPrint,
    });
    if (!confirmed) return;

    downloadPDF({
      routeIds: routeIdsToPrint,
      ...(confirmed?.includeMap ? { includeMap: true } : {}),
    });
  };

  const toggleRouteSelection = (routeId) => {
    setSelectedRouteIds((previous) => {
      const next = new Set(previous);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });
  };

  const renderGroupTree = (season) => {
    const seasonRoutes = visibleRoutes.filter(
      (route) => route.season === season,
    );
    if (seasonRoutes.length === 0 && transientGroups[season].size === 0) {
      return null;
    }
    return (
      <RoutesGroupTree
        key={season}
        season={season}
        routes={seasonRoutes}
        transientPaths={transientGroups[season]}
        selectedRouteIds={selectedRouteIds}
        busy={groupsBusy || deletingRoutes || pendingRouteId !== null}
        pendingRouteId={pendingRouteId}
        onSelectionChange={setSelectedRouteIds}
        onAddGroup={(parentPath, siblings) =>
          openAddGroup(season, parentPath, siblings)
        }
        onRenameGroup={(node, siblings) =>
          openRenameGroup(season, node, siblings)
        }
        onDeleteGroup={(node) => void deleteGroup(season, node)}
        onMoveRoute={(route, sourcePath, targetPath) =>
          void moveRoute(route, sourcePath, targetPath)
        }
        onReorderRoute={reorderRoute}
        onOpenRoute={openRoute}
      />
    );
  };

  const headerButtons = [];

  if (routeBuilderWeb && selectedRoutes.length > 0) {
    headerButtons.push({
      as: "button",
      color: "red",
      className: "w-28 h-10 whitespace-nowrap",
      disabled: groupsBusy || deletingRoutes || pendingRouteId !== null,
      label: `Delete (${selectedRoutes.length})`,
      onClick: handleDeleteRoutes,
      testId: "delete-routes-button",
    });
  }

  if (routeBuilderWeb && selectedRouteIds.size > 0) {
    headerButtons.push({
      as: "button",
      color: "blue",
      disabled:
        Boolean(selectionIssue) ||
        groupsBusy ||
        deletingRoutes ||
        pendingRouteId !== null,
      label: `Edit in Route Builder (${selectedRouteIds.size})`,
      onClick: () => void openBuilderEdit(),
      testId: "edit-in-route-builder-button",
      title: selectionIssue ?? undefined,
    });
  }

  if (routeBuilderWeb && selectedRouteIds.size > 0) {
    headerButtons.push({
      as: "button",
      color: "blue",
      disabled: groupsBusy || deletingRoutes || pendingRouteId !== null,
      label: `PDF (${selectedRouteIds.size})`,
      onClick: () => void handleDownloadPDF(),
      testId: "print-routes-pdf-button",
    });
  }

  headerButtons.push({
    color: "blue",
    label: routeBuilderWeb ? "Create Route Manually" : "Create Route",
    onClick: () => setDisplayChooseSeasonModal(true),
  });

  if (routeBuilderWeb) {
    headerButtons.push({
      as: "button",
      className: "!px-4",
      color: "green",
      disabled: optimizationNoticeVisible,
      label: (
        <span className="inline-flex items-center gap-1.5">
          <img src={SparkAiIcon} alt="" className="size-4 shrink-0" />
          <span>Generate Routes</span>
        </span>
      ),
      onClick: openBuilderSetup,
      testId: "generate-routes-open-button",
    });
  }

  return (
    <>
      <div className="flex flex-col items-center">
        <HeaderBar buttons={headerButtons} title="Routes" />
        {routeBuilderWeb ? (
          <>
            <RouteBuilderEntry
              ref={builderRef}
              renderButton={false}
              blankSeason={blankRouteSeason}
              onBlankSessionStarted={() => setBlankRouteSeason(null)}
              onOptimizationNoticeChange={setOptimizationNoticeVisible}
              onRoutesCreated={() => {
                setSelectedRouteIds(new Set());
                void refreshRoutes();
              }}
            />
            <div id="route-builder-optimization-notice" className="w-full" />
          </>
        ) : null}
        <div className="grid w-full grid-cols-1 px-4 pt-4 lg:grid-cols-2 lg:px-10">
          <SearchBarWithFilters
            onSearch={setSearchPhrase}
            value={searchPhrase}
            placeholder="Search routes..."
            autoFocus={false}
          >
            <FilterBar
              currentFilter={currentFilter}
              filters={[
                { label: "All", value: "all" },
                { label: "Landscape", value: Season.landscape },
                { label: "Snow", value: Season.snow },
              ]}
              setCurrentFilter={setCurrentFilter}
            />
          </SearchBarWithFilters>
          <div className="flex w-full lg:items-end">
            <StartARouteCard />
          </div>
        </div>

        {/* A failed background refresh should not hide routes already in the cache. */}
        {routesQuery.isLoading ? (
          <LoadingIcon />
        ) : routesQuery.isError && routesQuery.data === undefined ? (
          <p className="p-8 text-sm text-rose-700">Unable to load routes.</p>
        ) : (
          <div className="flex w-full flex-1 flex-col items-center overflow-y-scroll px-4 pb-8 lg:px-10">
            {routesQuery.isError ? (
              <div
                role="status"
                className="mb-3 flex w-full items-center gap-2 text-sm text-rose-700"
              >
                <span>
                  Unable to refresh routes. Showing the last available list.
                </span>
                <button
                  type="button"
                  className="font-semibold underline"
                  disabled={routesQuery.isFetching || pendingRouteId !== null}
                  onClick={() => void refreshRoutes()}
                >
                  {routesQuery.isFetching ? "Retrying..." : "Retry"}
                </button>
              </div>
            ) : null}
            {routeBuilderWeb ? (
              <div
                className="mb-3 flex w-full flex-col gap-2"
                data-testid="routes-selection-toolbar"
              >
                <div className="flex w-full flex-wrap items-center gap-2">
                  <div
                    className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5"
                    role="group"
                    aria-label="Routes layout"
                  >
                    {[
                      ["groups", "Groups"],
                      ["list", "List"],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        data-testid={`routes-view-${value}`}
                        aria-pressed={layout === value}
                        className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                          layout === value
                            ? "bg-snow-blue-3 text-white"
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                        onClick={() => setLayout(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {groupsBusy ? (
                    <span className="text-xs text-sky-700">
                      Updating Groups…
                    </span>
                  ) : null}
                </div>
                {selectionIssue ? (
                  <span
                    className="self-end text-xs text-rose-700"
                    data-testid="route-selection-issue"
                  >
                    {selectionIssue}
                  </span>
                ) : null}
              </div>
            ) : null}

            {routeBuilderWeb && layout === "groups" ? (
              <div
                className="flex w-full flex-col gap-5 rounded-xl border border-slate-200 bg-white p-3"
                data-testid="routes-groups-view"
              >
                <DraftsList
                  companyId={companyId ?? ""}
                  onOpenDraft={(id) => builderRef.current?.openDraft(id)}
                />
                {(currentFilter === "all" ||
                  currentFilter === Season.landscape) &&
                  renderGroupTree(Season.landscape)}
                {(currentFilter === "all" || currentFilter === Season.snow) &&
                  renderGroupTree(Season.snow)}
                {visibleRoutes.length === 0 &&
                transientGroups[Season.landscape].size === 0 &&
                transientGroups[Season.snow].size === 0 ? (
                  <p className="text-sm text-slate-500">
                    No routes match filters.
                  </p>
                ) : null}
              </div>
            ) : (
              <div
                className="flex w-full flex-col gap-8"
                data-testid="routes-list-view"
              >
                <FlatSeasonSection
                  season={Season.landscape}
                  routes={visibleRoutes
                    .filter((route) => route.season === Season.landscape)
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))}
                  selectedRouteIds={selectedRouteIds}
                  onToggleSelection={toggleRouteSelection}
                  onOpen={openRoute}
                  onReorder={handleReorder}
                  selectable={routeBuilderWeb}
                />
                <FlatSeasonSection
                  season={Season.snow}
                  routes={visibleRoutes
                    .filter((route) => route.season === Season.snow)
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))}
                  selectedRouteIds={selectedRouteIds}
                  onToggleSelection={toggleRouteSelection}
                  onOpen={openRoute}
                  onReorder={handleReorder}
                  selectable={routeBuilderWeb}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <TextInputModal
        open={Boolean(nameModal)}
        title={nameModal?.kind === "rename" ? "Rename Group" : "New Group"}
        description="Use letters, numbers, hyphens, and underscores. Add on a Group to create a nested Group."
        label="Group name"
        defaultValue={nameModal?.defaultValue ?? ""}
        confirmLabel={nameModal?.kind === "rename" ? "Rename" : "Create"}
        testIdPrefix="group-name"
        onCancel={() => setNameModal(null)}
        onSave={saveGroupName}
      />

      <MultipleChoiceModal
        closeModal={() => setDisplayChooseSeasonModal(false)}
        open={displayChooseSeasonModal}
        options={[
          {
            color: "green",
            label: "Landscape",
            onClick: () => {
              setDisplayChooseSeasonModal(false);
              if (routeBuilderWeb) {
                setBlankRouteSeason(Season.landscape);
              } else {
                navigate("/admin/route/new?season=landscape");
              }
            },
          },
          {
            color: "blue",
            label: "Snow",
            onClick: () => {
              setDisplayChooseSeasonModal(false);
              if (routeBuilderWeb) {
                setBlankRouteSeason(Season.snow);
              } else {
                navigate("/admin/route/new?season=snow");
              }
            },
          },
        ]}
        subtitle="Choose a season to create a new route for."
        title="Choose Season"
      />
    </>
  );
};

const mapStateToProps = (state) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(AdminRoutesPage);
